"use client"

import * as React from "react"
import { useState } from "react"

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from "@kit/ui/drawer"
import { CircleX } from "lucide-react"
import { Tables } from "~/lib/database.types"
import { formatCurrencyByCode } from "~/lib/currency-utils"
import { MarkdownRenderer } from "./markdown-renderer"
import Image from "next/image"
import { Button } from "@kit/ui/button"

export function ContentDetailDrawer({
  children,
  content
}: React.PropsWithChildren<{
  content: Tables<"general_contents">
}>) {
  const [isOpen, setOpen] = useState(false);

  return (
    <Drawer dismissible={false} open={isOpen} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        {children}
      </DrawerTrigger>
      <DrawerPortal>
        <DrawerOverlay className="fixed inset-0 bg-black/40" />
        <DrawerContent className="flex flex-col rounded-t-[10px] mt-24 fixed bottom-0 left-0 right-0 outline-none h-svh z-50" hideHandle>
          <DrawerHeader className="container max-w-4xl mt-6">
            <div className="justify-between flex flex-row">
              <div className="flex flex-col space-y-2 items-start">
                <DrawerTitle>
                  {content.title}
                </DrawerTitle>
                <DrawerDescription>
                  {formatCurrencyByCode(content.price, content.currency)}
                </DrawerDescription>
              </div>
              <CircleX size={32} onClick={() => setOpen(false)}/>
            </div>
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col max-w-4xl container my-6 space-y-6">
              {(content.image_urls?.length ?? 0) > 0 && (
                <div className="flex flex-row overflow-x-auto space-x-2">
                  {content.image_urls?.map((url, index) => {
                    return (
                    <Image
                      key={index}
                      src={url}
                      alt={content.title}
                      className="rounded-md my-4"
                      width={300}
                      height={300}
                    />
                    );
                  })}
                </div>
              )}
              {content.action_urls?.length > 0 && (
                <div className="flex flex-col space-y-2">
                  {content.action_urls.map((url, index) => {
                    return (
                      <Button
                        key={index}
                        variant="default"
                        className="rounded-full truncate"
                        onClick={() => {
                          if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                            window.open(url, '_blank');
                          } else {
                            console.error("[ContentDetailDrawer] Err: Invalid action URL");
                          }
                        }}
                      >
                        {content.action_titles[index] ?? `Link ${index + 1}`}
                      </Button>
                    )
                  })}
                </div>
              )}
              <MarkdownRenderer markdown={content.description}/>
            </div>
          </div>
        </DrawerContent>
      </DrawerPortal>
    </Drawer>
  )
}